import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';

import { ApiModule, Configuration, ConfigurationParameters } from '@bms-client/api';
import {
    applicationSettingsReducer,
    CURRENT_USER_CLEAR_ACTION_NAME,
    CURRENT_USER_UPDATE_PROFILE_DATA_ACTION_NAME,
    currentStructureTreeReducer,
    currentUserReducer,
    DummyEffects,
    StructureTreeEffects,
} from '@bms-client/store';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer } from '@ngrx/router-store';
import { MetaReducer, StoreModule } from '@ngrx/store';

import { StorageSync } from '@oc-core/ngrx-extensions';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { AssignStructureTreeDialogComponent } from './components/dialogs/assign-structure-tree-dialog/assign-structure-tree-dialog.component';
import { SaveAbortLineModule } from './components/save-abort-line/save-abort-line.module';
import { StructureTreeModule } from './components/structure-tree/structure-tree.module';
import { AddAuthTokenInterceptor } from './interceptor/add-auth-token.interceptor';
import { NetworkInterceptor } from './interceptor/network.interceptor';
import { SnackBarInterceptor } from './interceptor/snack-bar-interceptor';
import { getGermanPaginatorIntl } from './intl/german-paginator-intl';
import { DefaultModule } from './layouts/default/default.module';
import { EmptyModule } from './layouts/empty/empty.module';

export function apiConfigFactory(): Configuration {
    const params: ConfigurationParameters = {
        basePath: environment.endpoint,
        withCredentials: false,
    };
    return new Configuration(params);
}

export const bmsRootReducer = {
    applicationSettings: applicationSettingsReducer,
    currentUser: currentUserReducer,
    currentStructureTree: currentStructureTreeReducer,

    // Technically needed reducer
    router: routerReducer,
};

export const bmsRootEffects = [StructureTreeEffects];

const metaReducers: MetaReducer[] = [
    StorageSync({
        storageKeyPrefix: 'bms',
        subStores: {
            currentUser: {
                write: [CURRENT_USER_UPDATE_PROFILE_DATA_ACTION_NAME],
                clear: [CURRENT_USER_CLEAR_ACTION_NAME],
            },
        },
    }),
];

@NgModule({
    declarations: [
        AppComponent, //
        AssignStructureTreeDialogComponent,
    ],
    imports: [
        BrowserModule, //
        BrowserAnimationsModule,
        RouterModule.forRoot(appRoutes, {
            initialNavigation: 'enabledBlocking',
            enableTracing: false,
            bindToComponentInputs: true,
        }),

        DefaultModule,
        EmptyModule,

        ApiModule.forRoot(apiConfigFactory),
        StoreModule.forRoot(bmsRootReducer, {
            metaReducers,
        }),
        EffectsModule.forRoot([DummyEffects, ...bmsRootEffects]),

        HttpClientModule,
        MatInputModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        SaveAbortLineModule,
        StructureTreeModule,
        MatSnackBarModule,
        MatIconModule,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SnackBarInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AddAuthTokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NetworkInterceptor,
            multi: true,
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'outline' },
        },
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
        { provide: MatPaginatorIntl, useValue: getGermanPaginatorIntl() },
    ],
    bootstrap: [
        AppComponent, //
    ],
})
export class AppModule {}
